<template>
  <div class="device-showcase">
    <figure
      v-for="device in shown"
      :key="device.name"
      class="device"
      :class="'device--' + device.name"
    >
      <!-- What fills the glass: the capture once it has landed, and the slot that
           says one is coming until then. Both are inset into the frame's measured
           glass by `glass()` below, so the two are the same rectangle and a
           device gains its picture without the row moving. -->
      <img
        v-if="device.src"
        class="device-shot"
        :src="device.src"
        :alt="device.alt"
        loading="lazy"
        decoding="async"
        :style="glass(device.name)"
      />
      <screenshot-slot v-else class="device-slot" :style="glass(device.name)" />

      <!-- The frame is the device and is always drawn, over whatever filled it.
           Its glass is transparent, so what shows through is the capture or the
           slot and nothing has to sit behind either. A device waiting for its
           capture is still a device standing in the row, so the frame is not held
           back with the picture. -->
      <img class="device-frame" :src="geometry(device.name).frame" alt="" aria-hidden="true" />
    </figure>
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* A build on three devices: a phone, a laptop and a tablet standing in a row,
   all bottom-aligned, so a page can show the same work at each size. The line-up
   is the caller's and the catalog states it, so the row is read from the left,
   starting at the narrowest device.

   The frame is the content and the capture is what fills it. One composed image
   per project instead would need three files and would go stale the moment a
   capture was retaken; layered, a new capture is a new file and nothing else.

   Two things can fill the glass and only one of them ever does: the capture, or
   the shared screenshot slot the browser frame also draws when its view is empty.
   Both are inset into the measured glass, so they are the same rectangle and a
   device gains its picture without the row moving. The frame is then painted over
   whichever is there, and because its glass is transparent what shows through it
   is what filled it, so nothing sits behind the two.

   The frame used to be held back along with the capture, on the argument that a
   bezel around an empty screen reads as a broken phone. That is not what a slot
   in the glass reads as: it is the same mark the browser frame shows, and it says
   in so many words what it is waiting for. Holding the frame back also cost the
   row the device itself, and a phone whose capture has not arrived is still a
   phone standing in the row.

   Every device box is now its own shape rather than whatever its picture
   happened to be. `aspect-ratio` gives each figure the box its frame occupies,
   so the row is the same height whether a capture has landed or not and the slot
   stands inside the device it is waiting for rather than setting the row's
   height. Before this the phone and the laptop took their heights from the frame
   image inside them, which meant an unsupplied device had no height at all and
   the row could not be drawn without the file it was waiting for. */
.device-showcase {
  align-items: flex-end;
  display: flex;
  gap: 2%;
  justify-content: center;
}

.device {
  margin-bottom: 0;
  position: relative;
}

/* The crop is the top of the page: a capture is usually the top of the site, and
   its top is the part that recognises it.

   The two fills share one rectangle and one layer, so a capture and the slot
   standing in for it can never both be seen, and swapping one for the other moves
   nothing. The frame is the layer above them both. */
.device-shot,
.device-slot {
  position: absolute;
  z-index: 1;
}

.device-shot {
  object-fit: cover;
  object-position: top;
}

/* The slot arrives full-bleed from the browser frame, where it fills the view
   inside the chrome. The chrome here is the device's own frame, so the slot takes
   the measured glass instead: `inset: auto` clears the full-bleed default and the
   four edges come from the shared `glass()` binding, which is the binding the
   capture above already takes. */
.device-slot {
  inset: auto;
}

/* The drop shadow is a filter rather than the separate shadow PNGs the hero
   pairs with its frames, because a filter follows the frame's own alpha and so
   cannot fall out of registration with it. */
.device-frame {
  display: block;
  filter: drop-shadow(0 1.25rem 1.75rem rgba($ink, .22));
  position: relative;
  width: 100%;
  z-index: 2;
}

/* Bottom-aligned so the three stand on one line, and sized so the three are
   three clear sizes, in height as well as in width: the laptop the largest, the
   tablet the middle step and the phone the smallest.

   The rule is written as three sizes rather than as a step from the device
   before it, which is what it used to say. That phrasing was only true while the
   row happened to draw them largest first, and the line-up is the catalog's:
   the phone leads now, and a rule written against the sequence would have been
   wrong the moment it did. What the row has to keep is the ladder, not the order
   it is met in.

   The ladder is solved in the *heights*, because that is the axis the eye reads
   when three devices stand on one line, and the widths are what falls out of it.
   Each device declares its width and its own ratio, and a width times a ratio is
   a height:

     laptop   890 / 509 = 1.749   h = 1.749 x 60 = 34.3%
     tablet   550 / 388 = 1.418   h = 1.418 x 22 = 31.2%   (drawn upright)
     phone    439 / 220 = 1.995   h = 1.995 x 13 = 25.9%

   Two of those ratios are above 1 and one is below it, and that is the whole
   difficulty of this row. The laptop is landscape, so 60% of the container buys
   it 34% of height. The tablet is upright, so 22% of the container already buys
   it 31%. Its height is 2.48 times its own width, which caps that width hard: to
   stay under the laptop at 60% it cannot pass 24.2%, and at 24.2% the two heights
   are equal and read as one size. That cap is why the tablet is 22% rather than
   the 28% it had, and it is why this ladder has to be solved rather than nudged.
   The rule this row is built on is that the tablet is smaller than the laptop and
   bigger than the phone, so the tablet's width is the number that is not free and
   the laptop's is the one that is: the laptop takes the room left over, which is
   why it is the widest by a distance and the tablet reads as the middle step.

   The phone takes three of those points off the laptop, and that is the trade the
   paragraph above describes rather than a new one. At 10% it stood at 20% of the
   container, which is 36% below the tablet: the step was so much larger than the
   tablet's own that the third device stopped reading as the third size and
   started reading as a different scale. At 13% it stands at 25.9%, 17% below the
   tablet, and the two steps are of a size, which is what makes the row read as
   three sizes of one thing. The laptop pays for it in width, 63% to 60%, which is
   1.7 points of height it does not miss, because it is the one of the three whose
   size is free for the reason above.

   Bottom-aligned is what makes the smaller steps read as smaller: standing on one
   line, the three differ only in how far they rise, so the height ladder is the
   one comparison the row makes.

   The widths sum to 95% and the two 2% gaps take the rest, so the row leaves 1%
   of slack rather than landing exactly on 100% and risking a hair of overflow on
   a fractional container. */
.device--laptop {
  aspect-ratio: 890 / 509;
  width: 60%;
}

/* The tablet asset is landscape and a tablet is shown upright, so the figure is
   the box the frame occupies once it has been turned, which is the asset on its
   side: height over width 550/388, so its own height is 1.418 times the 22% the
   ladder gives it. That is the number the cap above is about.

   The frame fills that box from the inside. Its width is the figure's height,
   which is the 550/388 of the figure's width below, and its height falls out of
   the asset's own ratio, so centred and turned a quarter turn about its middle
   the frame lands exactly on the figure. Two boxes, one shape, which is what
   makes the shot's rectangle a mapping of the measured glass rather than a
   second set of measurements.

   `max-width: none` is load-bearing and is the reason this row was wrong for so
   long. `_reset.scss` sets `img { max-width: 100% }`, which is right for every
   other image on the site and silently wrong for this one: the frame has to be
   wider than its parent, because the parent is the upright box and the frame is
   the landscape one lying across it. At 100% the frame was clamped back to the
   figure's width, so it drew at 0.7055 of its size (388/550, the asset's ratio of
   the figure) while the shot kept the figure's real glass rectangle. The result
   was a tablet two thirds of its intended size with a white block showing all
   around it, because the shot was still the size the frame was supposed to be.
   Nothing warned: exceeding a `max-width` is not an error, the declaration just
   stops applying. This is the only image on the site that has to be wider than
   its container, so it is the only one that lifts the clamp.

   The shadow moves up to the figure, because a filter is applied before a
   transform on the same element: left on the frame it would be turned with it
   and the light would come from the left edge.

   Moving it up used to need a guard, because on the figure it falls over
   whatever the figure holds and the figure could hold a slot rather than a
   device. There is no guard now: the frame is always drawn, so the figure always
   holds a device and the shadow always has something to be cast by. */
.device--tablet {
  aspect-ratio: 388 / 550;
  filter: drop-shadow(0 1.25rem 1.75rem rgba($ink, .22));
  width: 22%;

  .device-frame {
    filter: none;
    left: 50%;
    max-width: none;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%) rotate(-90deg);
    width: 141.752%; /* 550 / 388 */
  }
}

/* The smallest of the three, and under the tablet in both directions. Its height
   follows from its own width more steeply than either of the others (1.995), so
   at 13% it stands at 25.9% of the container, 17% below the tablet.

   It was 10%, which is 20% of the container and 36% below the tablet, and a step
   that far down stopped reading as the third size and started reading as a
   different scale: on the row the phone was the object the eye skipped. The three
   points came from the laptop, which is the device this ladder leaves free. */
.device--phone {
  aspect-ratio: 220 / 439;
  width: 13%;
}

/* Below md the row is cut to one device, and the one kept is the laptop.

   Three devices across a phone screen leaves the laptop about 150px wide and the
   capture inside it unreadable. The old answer was a column of three, each
   scaled to its share of the container, which fixed the sizes but not the
   problem: it put three screenshots a reader cannot read in a run down one
   screen, and the two narrow ones were crops of a page the widest one already
   shows. A capture is here to be looked at, so the narrow screen draws the one
   that carries the most of the work at the size the container allows and drops
   the other two. It is the desktop capture because it is the whole composition:
   the tablet and the phone show the same pages narrowed, so they add a width
   rather than a screen.

   The two are hidden with `display: none` rather than left out of `shown`. The
   list the caller hands over is the catalog's statement of what the build was
   captured on, and it should stay the same on every screen; which of the three
   is worth the room is a layout decision, and the layout is the stylesheet's.
   Hidden this way there is no JS branch, no second code path to keep in step,
   and the row above md is untouched.

   Hiding also takes them off the network. Both captures are `loading="lazy"`,
   and a hidden lazy image has no box to intersect, so a phone never fetches the
   two files it will not draw. That is worth stating because it is the opposite
   of the usual cost of `display: none`, and it is what makes cutting them here
   cheaper than the column was.

   The laptop takes the container, which is the 100% the column already gave it:
   one device on its own line is the row's own largest step, so nothing about how
   it is drawn changes, only how many are drawn beside it. */
@media (max-width: 767.98px) {
  .device--tablet,
  .device--phone {
    display: none;
  }

  .device--laptop {
    width: 100%;
  }
}
</style>

<script>
import ScreenshotSlot from '@/Components/ScreenshotSlot.vue';

/* The frame asset and the glass rect for each slot, which is the only place
   either is written down.

   The rect is measured, not estimated: it is the alpha hole in the PNG, found by
   flood-filling the transparent pixels that touch the border and taking what is
   left, so it stays right for a frame asset that ships with padding or a baked
   shadow around the device. Held as percentages of the asset it holds at any
   width.

     laptop  890 x 509   glass x 100..788  y 31..463   (689 x 433)
     tablet  550 x 388   glass x 17..532   y 17..370   (516 x 354)
     phone   220 x 439   glass x 14..205   y 13..427   (192 x 415)

   `size` is the asset in its own pixels, and it is here for one reason: `BLEED`
   below is a distance in those pixels, and a distance only becomes a share once
   what it is a share of is known. It is not read for anything else, and the rect
   stays in percentages so the row still holds at any width.

   These are the three frames the hero already ships, and re-measuring the laptop
   and the phone reproduces what the mobile page documented for them. The tablet
   is the one drawn upright, so its four numbers are the ones in the asset and the
   quarter turn is applied where the shot is placed.

   The geometry stays here rather than in the page because the numbers only mean
   anything beside the file they were measured from: a caller who could pass a
   different frame would have to pass different percentages too, and nothing
   would check that the two matched. A frame asset that is ever replaced has to
   be measured again, and nothing in the build will notice if it is not. */
const GEOMETRY = {
  laptop: {
    frame: '/images/home/hero/laptop-frame.png',
    size: [890, 509],
    inset: { left: 11.24, top: 6.09, width: 77.42, height: 85.07 }
  },
  tablet: {
    frame: '/images/home/hero/tablet-frame.png',
    size: [550, 388],
    inset: { left: 3.09, top: 4.38, width: 93.82, height: 91.24 },
    /* Drawn on its side: the stylesheet turns the frame a quarter turn, so the
       figure and the frame are the same box and the shot is mapped into it. */
    upright: true
  },
  phone: {
    frame: '/images/home/hero/phone-frame.png',
    size: [220, 439],
    inset: { left: 6.36, top: 2.96, width: 87.27, height: 94.53 }
  }
};

/* How far the capture is grown past the measured glass on every side.

   The glass is the fully transparent hole and the frame's inner edge is not. The
   pixel just outside the hole is still partly opaque in all three assets (alpha
   13 in the tablet, 145 to 178 in the laptop and the phone) and the frame does
   not reach full opacity until three pixels out. A capture clipped exactly to the
   hole therefore leaves those pixels with nothing behind them, so the page shows
   through the bezel's own anti-aliasing and a hairline draws between the screen
   and the frame. It is a sub-pixel seam at the sizes this row draws, which is why
   it reads as a thin light line rather than as a gap.

   Growing the rectangle puts the capture's edge where the frame is already
   opaque, so the bezel covers the seam instead of the seam being guessed at. A
   pixel of margin over the widest rim measured (3px) is the whole of the number,
   and the narrowest bezel in the three assets is 11px, so a bleed can never
   reach the outside of a frame.

   This is a bleed rather than a background behind the glass for a reason: a
   ground would have to be the bezel's colour, which differs per asset and by
   shade within one, so it would be a colour matched to a file. The capture is
   already the right thing to put under the frame's edge. */
const BLEED = 4;

export default {
  components: {
    ScreenshotSlot
  },

  props: {
    /* [{ name: 'laptop' | 'tablet' | 'phone', src, alt }]. The order is the
       order drawn, so the caller decides the line-up. A device with an empty
       `src` is one whose capture has not been supplied: it is still drawn, as
       the slot the browser frame also draws, so a project shows the same three
       devices as every other one and gains its pictures as the files land. */
    devices: {
      type: Array,
      required: true
    }
  },

  computed: {
    /* Every device the frame table knows, in the order the caller handed them
       over. The name is the only thing a device needs to be drawable, which is
       what makes the table the gate and not the file, and it is why the box is
       the stylesheet's: a device that is waiting for a capture still has to
       stand in its place at its own size, so its shape cannot depend on the
       picture that has not arrived. */
    shown() {
      return this.devices.filter((device) => GEOMETRY[device.name]);
    }
  },

  methods: {
    geometry(name) {
      return GEOMETRY[name];
    },

    /* The inset as a style binding: these are the only per-device numbers, and
       they are already in the table above, so restating them in the stylesheet
       would be the same four values written twice.

       A quarter turn is the one thing that makes the shot's rectangle differ
       from the measured glass. It maps the glass onto itself with the sides
       swapped: what was the left inset becomes the top one, and the width and
       the height trade places. The shot is placed in the figure, which is the
       frame's box after the turn, so the swap is what puts it back inside the
       glass.

       The bleed is applied after the swap and in the figure's own axes, which is
       why the divisor turns with the mapping. Unswapped the figure's width
       carries the asset's width, so the horizontal bleed is a share of `size[0]`.
       Swapped the figure's width carries the asset's height instead, so the same
       share is taken of `size[1]` and the two are crossed. Taking both from
       `size[0]` reads as correct and is wrong by the asset's own ratio, which
       shows up as a seam on the tablet alone. */
    glass(name) {
      const { inset, upright, size } = GEOMETRY[name];
      const [assetWidth, assetHeight] = size;

      const bleed = upright
        ? { x: (BLEED / assetHeight) * 100, y: (BLEED / assetWidth) * 100 }
        : { x: (BLEED / assetWidth) * 100, y: (BLEED / assetHeight) * 100 };

      const box = upright
        ? { left: inset.top, top: inset.left, width: inset.height, height: inset.width }
        : inset;

      return {
        left: box.left - bleed.x + '%',
        top: box.top - bleed.y + '%',
        width: box.width + bleed.x * 2 + '%',
        height: box.height + bleed.y * 2 + '%'
      };
    }
  }
};
</script>
